import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { NotificationDot } from "@-/dev.improve.haniverse.components.ui.notification-dot";
import { Pressable, View, type AccessibilityProps } from "@-/com.npmjs.react-native";
import { GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import {
  createAnimatedComponent,
  useAnimatedStyle,
  useDerivedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { KeyboardStickyView } from "@-/com.npmjs.react-native-keyboard-controller";
import { SafeAreaView, useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useContext, type ReactNode } from "react";
import { useNavAccountDrawer } from "#nav-account-drawer-context";
import {
  ToolbarDrawerButtonHiddenContext,
  ToolbarGlassSurface,
  ToolbarLeadingSlotHiddenContext,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useSupportSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.support.navigator";
import { useHanCoinDepositTarget } from "@-/dev.improve.haniverse.components.ui.han-coin-deposit";
import { AdminPanelGesture } from "@-/dev.improve.haniverse.routes.nav._components.admin-panel";

const AnimatedView = createAnimatedComponent(View);

/**
 * A row's notification dot propagates to the button that opens the drawer. It hides while the
 * drawer is open: the button is then its close control, and the lit row itself is on screen.
 */
function useTriggerNotification() {
  const { hasNotifications, isOpen } = useNavAccountDrawer();
  const showsNotificationDot = hasNotifications && !isOpen;
  return {
    accessibilityValue: showsNotificationDot ? { text: "New" } : undefined,
    showsNotificationDot,
  };
}

/** The landscape drawer belongs to the navigation rail and uses the same 48px tab row. */
export function NavAccountDrawerTabButton() {
  const { closeDrawer, isOpen, openDrawer } = useNavAccountDrawer();
  const { accessibilityValue, showsNotificationDot } = useTriggerNotification();
  return (
    <Pressable
      accessibilityLabel={isOpen ? "Close profile drawer" : "Open profile drawer"}
      accessibilityRole="button"
      accessibilityValue={accessibilityValue}
      className="relative h-12 w-full items-center justify-center bg-background active:bg-accent"
      onPress={isOpen ? closeDrawer : openDrawer}
      testID="nav-profile-drawer-trigger"
    >
      <View className="relative">
        <Icon
          className="text-foreground"
          name={isOpen ? "TbLayoutSidebarRightFilled" : "TbLayoutSidebarRight"}
          size={24}
        />
        {showsNotificationDot ? (
          <NotificationDot
            className="absolute -top-0.5 -right-1"
            testID="nav-profile-drawer-trigger-notification-dot"
          />
        ) : null}
      </View>
    </Pressable>
  );
}

/**
 * The drawer stays in the top-right corner. In landscape the page control follows the camera
 * edge unless the page reserves its menu at the scene's left edge.
 * Learn keeps its menu to the left of the card in either landscape direction, with the drawer
 * above the right-side action buttons. Either corner hides, staying mounted, while the app-wide
 * `ToolbarDrawerButtonHiddenContext` / `ToolbarLeadingSlotHiddenContext` switch says so (the
 * toolbar rows take its lane then).
 */
export function NavAccountDrawerTrigger({
  landscapeLeadingRailWidth = 0,
  landscapeSceneRailWidth,
  leadingControl,
}: {
  /** Clear the navigation rail when a page pins its leading control to the left. */
  landscapeLeadingRailWidth?: number;
  /** Keep Learn's menu at the scene's left edge and its drawer at the scene's right edge. */
  landscapeSceneRailWidth?: number;
  leadingControl?: ReactNode;
} = {}) {
  const supportNavigator = useSupportSettingsPageNavigator();
  const isDrawerButtonHidden = useContext(ToolbarDrawerButtonHiddenContext);
  // The leading control stays mounted while hidden: it hosts teleported page content.
  const isLeadingControlHidden = useContext(ToolbarLeadingSlotHiddenContext);
  const { bottom, left, right, top } = useSafeAreaInsets();
  const { diameter, height, isLandscape, isLeadingSlotAtLeft } = useToolbarGridMetrics();
  const hasSceneLeadingSlot = isLandscape && landscapeSceneRailWidth !== undefined;
  const isRailLeft = isLandscape && right >= left;
  const isLeadingControlRight =
    isLandscape && right > left && !hasSceneLeadingSlot && !isLeadingSlotAtLeft;
  const { closeDrawer, drawerProgress, drawerWidth, isOpen, openDrawer } = useNavAccountDrawer();
  const { accessibilityValue, showsNotificationDot } = useTriggerNotification();
  // Earned Han drops into this button, bumping it with every coin.
  const depositTarget = useHanCoinDepositTarget();
  const shellTranslateX = useDerivedValue(() => drawerProgress.get() * -(drawerWidth - right));
  const triggerMotion = useAnimatedStyle(() => ({
    transform: [{ translateX: shellTranslateX.get() }],
  }));
  const helpMotion = useAnimatedStyle(() => ({
    opacity: Math.min(1, drawerProgress.get() * 10),
    transform: [{ translateX: -drawerProgress.get() * drawerWidth }],
  }));
  const renderDrawerButtonGlass = (props: AccessibilityProps, testID: string) => (
    <ToolbarGlassSurface className="size-full">
      <Pressable
        {...props}
        accessibilityLabel={isOpen ? "Close profile drawer" : "Open profile drawer"}
        accessibilityRole="button"
        accessibilityValue={accessibilityValue}
        className="size-full items-center justify-center"
        hitSlop={8}
        onPress={() => {
          if (isOpen) {
            closeDrawer();
          } else {
            openDrawer();
          }
        }}
        testID={testID}
      >
        <Icon
          className="text-foreground"
          name={isOpen ? "TbLayoutSidebarRightFilled" : "TbLayoutSidebarRight"}
          size={24}
        />
      </Pressable>
    </ToolbarGlassSurface>
  );

  return (
    <AdminPanelGesture
      // A top-placed Admin panel covers this corner, so it draws the drawer button again on itself.
      renderDrawerButton={(grip) => (
        <View className="relative" style={{ height: diameter, width: diameter }}>
          <GestureDetector gesture={grip}>
            <View className="size-full">
              {renderDrawerButtonGlass({}, "admin-panel-drawer-trigger")}
            </View>
          </GestureDetector>
          {showsNotificationDot ? (
            <NotificationDot
              className="absolute top-0.5 right-0.5"
              testID="admin-panel-drawer-trigger-notification-dot"
            />
          ) : null}
        </View>
      )}
      shellTranslateX={shellTranslateX}
    >
      {(adminGestureProps, adminGesture) => (
        <>
          <AnimatedView
            className="absolute inset-x-0 top-0 z-50"
            pointerEvents="box-none"
            style={triggerMotion}
          >
            <View
              pointerEvents="box-none"
              style={{ paddingLeft: left, paddingRight: right, paddingTop: top }}
            >
              <View
                className="flex-row items-start justify-between gap-2.25 px-2.25 pt-2.25"
                pointerEvents="box-none"
                style={{ height }}
              >
                {/* The drawer's own close affordance is the drawer button, so a back
              control behind the open panel would be both unreachable and misleading. Hide the
              slot rather than unmount it: it hosts teleported page content. */}
                {leadingControl != null ? (
                  <View
                    accessibilityElementsHidden={isOpen || isLeadingControlHidden}
                    className={`flex-row ${isOpen || isLeadingControlHidden ? "opacity-0" : ""} ${isLeadingControlRight ? "ml-auto" : ""}`}
                    importantForAccessibility={
                      isOpen || isLeadingControlHidden ? "no-hide-descendants" : "auto"
                    }
                    pointerEvents={isOpen || isLeadingControlHidden ? "none" : "box-none"}
                    style={{
                      height: diameter,
                      marginLeft:
                        isRailLeft && (hasSceneLeadingSlot || isLeadingSlotAtLeft)
                          ? (landscapeSceneRailWidth ?? landscapeLeadingRailWidth)
                          : undefined,
                    }}
                    testID="nav-account-drawer-leading-control"
                  >
                    {leadingControl}
                  </View>
                ) : null}
                <View
                  accessibilityElementsHidden={isDrawerButtonHidden}
                  className={
                    hasSceneLeadingSlot
                      ? `absolute top-2.25 right-2.25 ${isDrawerButtonHidden ? "opacity-0" : ""}`
                      : `relative ${leadingControl == null || !isLeadingControlRight ? "ml-auto" : ""} ${isDrawerButtonHidden ? "opacity-0" : ""}`
                  }
                  importantForAccessibility={isDrawerButtonHidden ? "no-hide-descendants" : "auto"}
                  pointerEvents={isDrawerButtonHidden ? "none" : "auto"}
                  ref={depositTarget.ref}
                  style={{
                    height: diameter,
                    marginRight:
                      hasSceneLeadingSlot && !isRailLeft ? landscapeSceneRailWidth : undefined,
                    width: diameter,
                  }}
                >
                  {/* Swiping down from the drawer button opens the app's one Admin panel. */}
                  <GestureDetector gesture={adminGesture}>
                    <AnimatedView
                      style={[{ height: diameter, width: diameter }, depositTarget.pulseStyle]}
                    >
                      {renderDrawerButtonGlass(adminGestureProps, "nav-profile-drawer-trigger")}
                    </AnimatedView>
                  </GestureDetector>
                  {/* Outside the glass surface so its rounded clip cannot cut the dot off. */}
                  {showsNotificationDot ? (
                    <NotificationDot
                      className="absolute top-0.5 right-0.5"
                      testID="nav-profile-drawer-trigger-notification-dot"
                    />
                  ) : null}
                </View>
              </View>
            </View>
          </AnimatedView>
          <KeyboardStickyView
            accessibilityElementsHidden={!isOpen}
            className="absolute inset-x-0 bottom-0 z-50"
            importantForAccessibility={isOpen ? "auto" : "no-hide-descendants"}
            // The keyboard covers the bottom safe area; cancel that inset while it is open.
            offset={{ opened: bottom }}
            pointerEvents={isOpen ? "box-none" : "none"}
          >
            <SafeAreaView edges={["bottom"]} pointerEvents="box-none">
              <View className="items-end px-2.25 pb-2.25" pointerEvents="box-none">
                <AnimatedView style={[{ height: diameter, width: diameter }, helpMotion]}>
                  <Pressable
                    accessibilityLabel="Help and support"
                    accessibilityRole="button"
                    className="size-full items-center justify-center rounded-full border border-border/60 bg-background active:bg-accent"
                    hitSlop={8}
                    onPress={() => {
                      supportNavigator.push.mutate();
                    }}
                    testID="nav-profile-drawer-help"
                  >
                    <Icon className="text-foreground" name="MdQuestionMark" size={24} />
                  </Pressable>
                </AnimatedView>
              </View>
            </SafeAreaView>
          </KeyboardStickyView>
        </>
      )}
    </AdminPanelGesture>
  );
}
